import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LanguageContext = createContext(null);

// Supported languages with display info
export const LANGUAGES = {
    nl: { code: 'nl', short: 'NL', name: 'Nederlands', flag: '🇳🇱' },
    fr: { code: 'fr', short: 'FR', name: 'Français', flag: '🇫🇷' },
    en: { code: 'en', short: 'EN', name: 'English', flag: '🇬🇧' }
};

const STORAGE_KEY = 'language';
const DEFAULT_LANGUAGE = 'nl';

/**
 * Detect browser language and match to supported languages
 */
function detectBrowserLanguage(supportedLanguages) {
    // Check navigator.language first (e.g., "nl-BE", "fr-FR", "en-US")
    const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
    if (browserLang && supportedLanguages.includes(browserLang)) {
        return browserLang;
    }

    // Check navigator.languages array for broader support
    if (navigator.languages) {
        for (const lang of navigator.languages) {
            const shortLang = lang.split('-')[0].toLowerCase();
            if (supportedLanguages.includes(shortLang)) {
                return shortLang;
            }
        }
    }

    return null;
}

/**
 * Get initial language from localStorage, browser detection, or default
 */
function getInitialLanguage(supportedLanguages, defaultLanguage) {
    // 1. Check localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && supportedLanguages.includes(stored)) {
        return stored;
    }

    // 2. Check browser language
    const browserLang = detectBrowserLanguage(supportedLanguages);
    if (browserLang) {
        return browserLang;
    }

    // 3. Fall back to default
    return defaultLanguage;
}

export function LanguageProvider({ children, supportedLanguages, defaultLanguage }) {
    // Use provided values or defaults - useMemo to prevent reference changes
    const languages = React.useMemo(() =>
        supportedLanguages || Object.keys(LANGUAGES),
        [supportedLanguages]
    );
    const fallbackLanguage = defaultLanguage || DEFAULT_LANGUAGE;

    const [language, setLanguageState] = useState(() =>
        getInitialLanguage(languages, fallbackLanguage)
    );

    // Update localStorage when language changes
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, language);
        // Dispatch custom event so other components can react
        window.dispatchEvent(new CustomEvent('languageChange', { detail: { language } }));
    }, [language]);

    // Set language with validation
    const setLanguage = useCallback((lang) => {
        if (languages.includes(lang)) {
            console.log('Setting language to:', lang);
            setLanguageState(lang);
        } else {
            console.warn(`Language "${lang}" is not supported. Supported: ${languages.join(', ')}`);
        }
    }, [languages]);

    // Get language info object
    const getLanguageInfo = useCallback((lang = language) => {
        return LANGUAGES[lang] || { code: lang, short: lang.toUpperCase(), name: lang, flag: '' };
    }, [language]);

    const value = React.useMemo(() => ({
        language,
        setLanguage,
        supportedLanguages: languages,
        defaultLanguage: fallbackLanguage,
        getLanguageInfo,
        languageInfo: getLanguageInfo(language)
    }), [language, setLanguage, languages, fallbackLanguage, getLanguageInfo]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}

export default LanguageContext;
